import { createContext, useContext } from "react";
import {
  WALKTHROUGH_PAGE_NAME_COPY,
  WALKTHROUGH_PAGE_START_STEP,
  WALKTHROUGH_THIS_PAGE_COPY,
} from "@fomofi/shared";
import { clampStep } from "./walkthrough";
import { openWalkthroughSession, useWalkthroughSavedStep } from "./walkthroughStore";

/**
 * The seam between the sign-up pages and the "How Fomo-Fi works" walkthrough
 * (Anthony's doc 3, "The walkthrough overlay"). The sign-up pages need two things:
 *
 * - `openWalkthrough(step, from)`: open it at the step that fits the page (C8: pages 1–3
 *   at step 1, Analysis at step 2), over the named page ("Back to [page]", E6). It's an
 *   event, so this file has no dependency on the overlay: `WalkthroughHost` listens for
 *   `WALKTHROUGH_OPEN_EVENT` and opens at exactly that step.
 * - the step the user closed it at, so the link can read "Continue how it works
 *   (step N of 10)" (C9). `WalkthroughHost` publishes it: on the account when signed in
 *   (`walkthrough_last_step`, G2), in this browser before there's an account.
 *   `WalkthroughResumeContext` overrides it (tests, previews).
 */
export const WALKTHROUGH_OPEN_EVENT = "fomofi:walkthrough-open";

export type WalkthroughOpenDetail = { step?: number; from?: string; startStep?: number };

/** `startStep` is where this opener starts with nothing saved (the page's step); it
 *  defaults to `step`, which is right for a deep link. Closing saves only past it (R6). */
export function openWalkthrough(step?: number, from?: string, startStep?: number): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent<WalkthroughOpenDetail>(WALKTHROUGH_OPEN_EVENT, {
      detail: { step, from, startStep },
    }),
  );
}

/** An override for the step to resume at (1–10), or null for none. Undefined means
 *  "use the saved step". */
export const WalkthroughResumeContext = createContext<number | null | undefined>(undefined);

export function useWalkthroughLastStep(): number | null {
  const override = useContext(WalkthroughResumeContext);
  const saved = useWalkthroughSavedStep();
  const step = override === undefined ? saved : override;
  return typeof step === "number" && step >= 1 && step <= 10 ? step : null;
}

const SIGNUP_PAGES = ["account", "payment", "bank", "analysis"] as const;

/** Which step a sign-up page's link opens with nothing saved (R6): Analysis (page 4) at
 *  step 2, the rest at step 1. */
export function walkthroughStepForSignupPage(page: 1 | 2 | 3 | 4): number {
  return WALKTHROUGH_PAGE_START_STEP[SIGNUP_PAGES[page - 1]];
}

/** What "Back to [page]" calls each sign-up page (addendum R5, the prototype's `where`):
 *  Create account, your free trial, Connect bank, your analysis. */
export function walkthroughPageNameForSignupPage(page: 1 | 2 | 3 | 4): string {
  return WALKTHROUGH_PAGE_NAME_COPY[SIGNUP_PAGES[page - 1]].name;
}

/** What WalkthroughHost does with a bridge open: the caller already chose the step (the
 *  page's, or the saved one for "Continue"), so it opens exactly there. */
export function openFromBridge(detail: WalkthroughOpenDetail | undefined): void {
  const step = clampStep(detail?.step ?? 1);
  openWalkthroughSession({
    step,
    from: detail?.from ?? WALKTHROUGH_THIS_PAGE_COPY,
    startStep: clampStep(detail?.startStep ?? step),
  });
}
